<template>
  <Layout>
    <div class="payment-flow-container">
      <el-steps :active="currentStepNumber" finish-status="success" simple style="margin-bottom: 30px">
        <el-step v-for="(s, i) in displaySteps" :key="i" :title="s" />
      </el-steps>

      <div v-if="isLoading" v-loading="isLoading" element-loading-text="載入中..." style="margin-top: 35%;"></div>
      <transition name="fade-transform" mode="out-in">
        <component
          v-if="!isLoading && paymentInfo"
          :is="currentStepComponent"
          :key="currentStepComponent"
          :source="source"
          :paymentInfo="paymentInfo"
          :productId="productId"
          v-bind="componentProps"
          @next="handleNext"
          @back="handleBack"
          @redirect="handleRedirect"
          @fetchPayMethod="handlePaymentClick"
        />
      </transition>
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/main'
import { getMerchantOrderPaymentInfo, updateMerchantOrderPaymentMethod } from '@/src/apis'

import Step1Frequency from './components/Step1Frequency'
import Step2Method from './components/Step2Method'
import Step3OfflineType from './components/Step3OfflineType'
import Step3OnlineType from './components/Step3OnlineType'
import Step4OffLineForm from './components/Step4OffLineForm'
import Step4OnlineForm from './components/Step4OnlineForm'

export default {
  name: 'PaymentMain',
  components: {
    Layout,
    Step1Frequency,
    Step2Method,
    Step3OfflineType,
    Step3OnlineType,
    Step4OffLineForm,
    Step4OnlineForm,
  },
  props: ['source', 'containerId'],
  data() {
    return {
      paymentInfo: null,
      isLoading: false,
      productId: null,
    }
  },
  computed: {
    // 1. 根據 query 判斷目前該顯示哪一個組件
    // Payment/index.vue 的 computed 部分
    currentStepComponent() {
      const { frequency, method, type } = this.$route.query

      if (!frequency) return 'Step1Frequency' // 單次/分期
      if (!method) return 'Step2Method' // 線上/線下/儲值金

      // 如果選了線下 (method === 'offline') 且還沒選具體方式 (type)
      if (method === 'offline' && !type) return 'Step3OfflineType'

      // 如果選了線上 (method === 'online') 且還沒選具體方式 (type)
      if (method === 'online' && !type) return 'Step3OnlineType'

      // 最後一步：根據 type 和 method 顯示對應的表單
      if (type) {
        if (method === 'online') return 'Step4OnlineForm'
        return 'Step4OffLineForm'
      }

      return 'Step1Frequency'
    },
    displaySteps() {
      const method = this.$route.query.method
      if (method === 'offline') return ['選擇頻率', '付款方式', '確認方式', '確認資料']
      if (method === 'online') return ['選擇頻率', '付款方式', '確認方式', '確認資料']
      return ['選擇頻率', '付款方式']
    },
    currentStepNumber() {
      const { frequency, method, type } = this.$route.query
      let idx
      if (!frequency) idx = 0
      else if (!method) idx = 1
      else if ((method === 'offline' || method === 'online') && !type) idx = 2
      else if (type) idx = 3
      else idx = this.displaySteps.length - 1

      if (idx < 0) idx = 0
      if (idx > this.displaySteps.length - 1) idx = this.displaySteps.length - 1
      return idx
    },

    // 統一傳給子元件的資料 (props)
    componentProps() {
      return {
        source: this.$route.params.source,
        containerId: this.$route.params.containerId,
        // 把目前網址所有參數都傳下去，子元件就不用自己抓 $route
        ...this.$route.query,
      }
    },
  },
  async created() {
    await this.fetchPaymentInfo()
    this.productId = this.$route.query.productId
  },
  methods: {
    // 統一處理「下一步」：把 key/value 寫入網址
    handleNext({ key, value }) {
      this.$router
        .push({
          query: { ...this.$route.query, [key]: value },
        })
        .catch(() => {})
    },
    // 統一處理「回上一步」：移除最後一個參數
    handleBack(keyToRemove) {
      const newQuery = { ...this.$route.query }
      delete newQuery[keyToRemove]
      this.$router.push({ query: newQuery })
    },
    handleRedirect(productId) {
      if (this.source === 'hotel') {
        this.$router.push({
          name: 'hotelViewOrder',
          params: { productId: productId, containerId: this.containerId },
        })
      }
      this.paymentInfo = null
    },
    async fetchPaymentInfo() {
      this.isLoading = true
      try {
        const res = await getMerchantOrderPaymentInfo(this.containerId)
        this.paymentInfo = res
      } catch (error) {
        console.error('取得付款資訊失敗', error)
      } finally {
        this.isLoading = false
      }
    },
    canAffordWithDeposit() {
      const points = Number(this.paymentInfo?.deposit?.total || 0)
      const totalRequired = Number(this.paymentInfo?.total || 0)

      if (points < totalRequired) {
        //TODO: 儲存儲值金
        this.$message.warning('儲值餘額不足，請先充值或更換付款方式')
        return false
      }
      return true
    },
    hasValidPaymentStatus(status) {
      return status !== undefined && status !== null && status !== ''
    },
    parseFetchPayMethodPayload(data, invoiceData, paymentStatus) {
      // 相容兩種 emit 格式：
      // 1) fetchPayMethod(type, invoiceData, paymentStatus)
      // 2) fetchPayMethod({ paymentMethod, invoice, paymentStatus })
      if (data && typeof data === 'object') {
        const objectHasStatus = Object.prototype.hasOwnProperty.call(data, 'paymentStatus') && this.hasValidPaymentStatus(data.paymentStatus)

        return {
          type: data.paymentMethod,
          invoice: data.invoice || null,
          paymentStatus: data.paymentStatus,
          hasPaymentStatus: objectHasStatus,
        }
      }

      return {
        type: data,
        invoice: invoiceData || null,
        paymentStatus,
        hasPaymentStatus: this.hasValidPaymentStatus(paymentStatus),
      }
    },
    toApiPaymentStatus(paymentStatus) {
      return String(paymentStatus).toUpperCase() === 'PAID' ? 'PAID' : 'UNPAID'
    },
    async handlePaymentClick(data, invoiceData, paymentStatus) {
      const { type, invoice, paymentStatus: status, hasPaymentStatus } = this.parseFetchPayMethodPayload(data, invoiceData, paymentStatus)

      const isConfirmed = await this.confirmPayment(type)
      if (!isConfirmed) return

      if (type === 'Deposit' && !this.canAffordWithDeposit()) return

      await this.fetchUpdatePayMethod(type, invoice, status, hasPaymentStatus)
    },
    async fetchUpdatePayMethod(type, invoiceData = null, paymentStatus, hasPaymentStatus = false) {
      this.isLoading = true
      const requestPaymentStatus = this.toApiPaymentStatus(paymentStatus)
      let payload = {
        paymentMethod: type,
      }

      if (type === 'Deposit') {
        payload = { paymentMethod: type, total: this.paymentInfo?.total }
      } else if (type === 'Credit') {
        payload = { paymentMethod: 'Credit', total: this.paymentInfo?.total }
      } else if (type === 'Vacc') {
        payload = {
          paymentMethod: 'Vacc',
          total: this.paymentInfo?.total,
          bankType: this.$route.query.bankType,
        }
      }
      // 線下付款方式：加入發票資訊
      else if (['StoreCredit', 'Atm', 'Cash', 'StoreLinePay', 'unspecified'].includes(type)) {
        if (hasPaymentStatus) {
          payload.paymentStatus = requestPaymentStatus
        }
        if (invoiceData) {
          payload.invoice = invoiceData
        }
      }

      try {
        await updateMerchantOrderPaymentMethod(this.containerId, payload)
        this.$message.success('付款資訊已記錄')
        this.handleRedirect(this.productId)
      } catch (error) {
        this.$message.error('提交失敗，請稍後再試')
      } finally {
        this.isLoading = false
      }
    },
    async confirmPayment(type) {
      const config = {
        StoreCredit: { title: '現場信用卡確認', msg: '您確定選擇「現場信用卡」嗎？需至現場完成支付。', btn: '確認選擇' },
        Atm: { title: '線下匯款確認', msg: '您確定選擇「線下匯款」嗎？需至現場完成支付。', btn: '確認選擇' },
        Cash: { title: '現金付款確認', msg: '您確定選擇「現金付款」嗎？需至現場完成支付。', btn: '確認選擇' },
        StoreLinePay: { title: 'LINE Pay 確認', msg: '您確定選擇「LINE Pay」嗎？需至現場完成支付。', btn: '確認選擇' },
        Credit: { title: '線上信用卡確認', msg: '將寄送付款資訊至用戶Email，進行線上刷卡支付。', btn: '前往支付' },
        Vacc: { title: '虛擬帳戶確認', msg: '即將產生虛擬帳戶以進行轉帳。', btn: '前往支付' },
        Deposit: { title: '儲值金抵扣', msg: `您確定要使用儲值點數抵扣 <b style="color:#f56c6c;">NT$ ${this.paymentInfo.total}</b> 嗎？`, btn: '確定抵扣' },
      }

      const target = config[type] || { title: '付款確認', msg: '確定進行付款嗎？', btn: '確定' }
      try {
        await this.$confirm(target.msg, target.title, {
          confirmButtonText: target.btn,
          cancelButtonText: '再想想',
          type: 'warning',
          dangerouslyUseHTMLString: true,
          center: true,
        })
        return true
      } catch (e) {
        return false
      }
    },
  },
}
</script>

<style scoped>
.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all 0.3s;
}
.fade-transform-enter {
  opacity: 0;
  transform: translateX(20px);
}
.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
